import { Component, EventEmitter, Input, Output, signal, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, NgModel } from '@angular/forms';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { minutesToHoursAndMinutes } from '@app/modules/time-tracking/functions/minutes-to-hours-and-minutes';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Locale } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import { addDays } from 'date-fns';
import moment, { Moment } from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EndTimeValidator } from '../../directives';
import { PayrollHourType, Settings } from '../../interfaces/settings.interface';
import { EnteringFor } from '../../interfaces/timesheet-entering-for.type';
import { INCOME_TYPES } from '../../meta/time-tracking-payroll-income-types';
import { EmployeeHourTypesService } from '../../services/employee-hour-types.service';
import { TimeTrackingSettingsService } from '../../services/time-tracking-settings.service';
import { TimeFormatValidator } from './../../directives/time-format-validator.directive';
import { generateTimeIntervals } from './../../utils/time.utils';

const fullTimeRegex = /^\d{1,2}:\d{2}$/;
const oneDigitWithOptionalColon = /^\d:?$/;
const twoDigitsWithOptionalColon = /^\d{2}:?$/;
const twoDigitsAfterColon = /^:\d{2}$/;
const threeOrFourDigits = /^\d{3}$/;

@Component({
    selector: 'app-enter-hours-card',
    templateUrl: './enter-hours-card.component.html',
    styleUrls: ['./enter-hours-card.component.scss'],
    providers: [{ provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher }],
})
export class EnterHoursComponentCard extends BaseForm {
    @ViewChild('timeInput') timeInput!: NgModel;
    @ViewChild('startTime') startTimeInput!: NgModel;
    @ViewChild('endTime') endTimeInput!: NgModel;

    @Input() timesheetEntry = new TimesheetEntry();
    @Input() employees: Employee[] = [];

    /**
     * enteringFor is a string that determines whether or not to display the employee select
     * 'self' - do not display the employee select
     * 'other' - display the employee select but do not allow the user to change the employee
     * 'many' - display the employee select and allow the user to change the employee
     */
    @Input() enteringFor: EnteringFor = 'self';

    @Input() showRemove = false;

    @Input() defaultApprovalFlow: 'manager' | 'automatic' = 'manager';

    @Input() companyIsOnPayrollIntegration = false;

    @Input() projectCanBeChanged = true;

    @Input() overlappingEntries: { start: number[]; end: number[] } = { start: [], end: [] };

    @Output() onRemoveEntry = new EventEmitter<TimesheetEntry>();

    /**
     * onChangeEmployeeOrWorkedOn emits whenever a timesheet is changed
     * with a new employee or workedOn date
     */
    @Output() onChangeEmployeeOrWorkedOn = new EventEmitter<TimesheetEntry>();

    @Output() onChangeMinutes = new EventEmitter<TimesheetEntry>();

    employeeFormControl = new FormControl<string | Employee>('');

    filteredEmployees: Observable<Employee[]> | null = null;

    /**
     * maxDate is the latest date that time and be entered for.
     * The default is today (ie. not in the future), but this can be changed in settings.
     */
    maxDate: Date = new Date();

    /**
     * allowNoProject determines if we show the "none" option for projects
     * some users cannot select no project for other users.
     * Ex: a project owner cannot set no project for someone,
     * unless they managed them (and the company's default flow is manager, etc. etc. etc...)
     *
     * This value will be different per card and must be updated when the employee changes.
     */
    allowNoProject = false;

    /**
     * hourTypes is the list of possible values for hour types
     */
    hourTypes: PayrollHourType[] = [];

    /**
     * selectedHourType is the hour type selected in the UI
     */
    selectedHourType: PayrollHourType | undefined;

    settings: Settings = {};

    timeIntervals: { value: string; label: string }[] = [];

    filteredStartTimes: { value: string; label: string }[] = [];
    filteredEndTimes: { value: string; label: string }[] = [];

    isAutocompleteSelected = false;

    // Signal to hold the formatted hours and minutes
    formattedHoursAndMinutes = signal('0h 0m');

    hasTimeClock = false;
    hasTimeClockTimer = false;

    isLoading = false;

    isDisabled = signal(false);

    private timeFormatValidator: TimeFormatValidator;
    private endTimeValidator = new EndTimeValidator();
    private currentLocale: Locale;

    constructor(
        public authService: AuthService,
        private employeeHourTypesService: EmployeeHourTypesService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private featureService: FeatureService,
        translateService: TranslateService
    ) {
        super();
        this.timeFormatValidator = new TimeFormatValidator(translateService);
        this.currentLocale = translateService.currentLang as Locale;
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;
        this.settings = await this.timeTrackingSettingsService.get();
        this.hasTimeClock = await this.featureService.has(FeatureFlag.timeClock);
        this.hasTimeClockTimer = await this.featureService.has(FeatureFlag.timeClockTimer);

        this.isDisabled.set(
            !!(
                this.hasTimeClock &&
                this.timesheetEntry.timeTrackingMethod &&
                this.timesheetEntry.timeTrackingMethod !== this.settings.timeTrackingMethod
            )
        );

        // Set timesheet entry's tracking method:
        // Use existing, fallback to company setting, or default to 'hours'
        // Ensures a valid tracking method is always set
        this.timesheetEntry.timeTrackingMethod = this.timesheetEntry.timeTrackingMethod
            ? this.timesheetEntry.timeTrackingMethod
            : this.settings.timeTrackingMethod
              ? this.settings.timeTrackingMethod
              : 'hours';

        this.updateFormattedHoursAndMinutes();
        this.handleFilteringEmployees();
        this.setMaxDate();

        if (this.hasTimeClock && this.timesheetEntry.timeTrackingMethod === 'start_end_time') {
            this.generateTimeIntervals();
            this.filteredStartTimes = this.timeIntervals.map((interval) => interval);
            this.filteredEndTimes = this.timeIntervals.map((interval) => interval);

            // Filter end times based on the initial start time if it exists
            if (this.timesheetEntry.startTime) {
                this.filterEndTimes(this.timesheetEntry.startTimeFormatted);
            }
        }

        this.isLoading = false;
    }

    async ngOnChanges(): Promise<void> {
        this.calculateAllowNoProject();
        this.adjustProject();
        this.setHourTypes();
    }

    async ngAfterViewInit(): Promise<void> {
        this.employeeFormControl.setValue(this.timesheetEntry.employee ?? '');
        if (this.enteringFor === 'other') {
            this.employeeFormControl.disable();
        }
    }

    async onChangeDate(date: Moment): Promise<void> {
        this.timesheetEntry.workedOn = date.format('YYYY-MM-DD');

        if (this.hasTimeClock && this.settings.timeTrackingMethod === 'start_end_time') {
            if (this.timesheetEntry.startTime) {
                this.timesheetEntry.startTime = moment(
                    `${date.format('YYYY-MM-DD')} ${this.timesheetEntry.startTimeFormatted}`,
                    'YYYY-MM-DD h:mm A'
                ).format('YYYY-MM-DD HH:mm:ss');
            }
            if (this.timesheetEntry.endTime) {
                this.timesheetEntry.endTime = moment(
                    `${date.format('YYYY-MM-DD')} ${this.timesheetEntry.endTimeFormatted}`,
                    'YYYY-MM-DD h:mm A'
                ).format('YYYY-MM-DD HH:mm:ss');
            }
        }

        this.setHourTypes();
        this.onChangeEmployeeOrWorkedOn.emit(this.timesheetEntry);
    }

    /**
     * onChangeEmployee ensures that the employee relationship is properly set on the
     * timesheet when the employeeId changes
     *
     * This allows us to access the relationship as needed.
     */
    onChangeEmployee(employeeId: number | null): void {
        // if you change the employee, reset the selected project
        this.timesheetEntry.project = null;

        if (!employeeId) {
            this.timesheetEntry.employee = null;
            this.clearHourTypeData();
            this.onChangeEmployeeOrWorkedOn.emit(this.timesheetEntry);
            return;
        }

        const employee = this.employees.find((e) => e.id === employeeId);

        if (!employee) {
            this.timesheetEntry.employee = null;
            this.clearHourTypeData();
            return;
        }

        this.timesheetEntry.employee = employee;
        this.setHourTypes();

        this.calculateAllowNoProject();
        this.adjustProject();
        this.onChangeEmployeeOrWorkedOn.emit(this.timesheetEntry);
    }

    /**
     * onChangeProject ensures that the project relationship is properly set on the
     * timesheet when the projectId changes
     *
     * This allows us to access the relationship as needed.
     */
    onChangeProject(projectId: number | null): void {
        if (!projectId) {
            this.timesheetEntry.project = null;
            return;
        }

        const projectAssignment = this.timesheetEntry.employee?.timeTrackingProjectAssignmentsUserCanEnterHoursFor.find(
            (assignment) => assignment.projectId === projectId
        );

        if (!projectAssignment) {
            this.timesheetEntry.project = null;
            return;
        }

        this.timesheetEntry.project = projectAssignment.project;
    }

    removeEntry(): void {
        this.onRemoveEntry.emit();
    }

    timeInputDisplayValue(): string {
        if (!this.timesheetEntry.minutes) {
            return '';
        }

        return minutesToHoursAndMinutes(this.timesheetEntry.minutes);
    }

    onTimeInputChange(time: string): void {
        this.timesheetEntry.minutes = this.getMinutesFromTimeString(time);
        this.onChangeMinutes.emit(this.timesheetEntry);
        if (this.timesheetEntry.minutes > 1440) {
            this.timeInput.control.setErrors({ tooManyMinutes: true });
            // force validation warning
            this.form.onSubmit(new SubmitEvent('submit'));
            return;
        }

        this.timeInput.control.setErrors(null);
    }

    getMinutesFromTimeString(time: string): number {
        if (!time) {
            return 0;
        }

        if (fullTimeRegex.test(time)) {
            const [hours, minutes] = time.split(':');
            return Number(hours) * 60 + Number(minutes);
        }

        if (oneDigitWithOptionalColon.test(time)) {
            const strippedTime = time.replace(/:$/, '');
            return Number(strippedTime) * 60;
        }

        if (twoDigitsWithOptionalColon.test(time)) {
            const strippedTime = time.replace(/:$/, '');
            const num = Number(strippedTime);

            if (num > 24) {
                // can't be hours, must be minutes
                return num;
            }

            // let's assume hours if it's 24 or fewer
            return num * 60;
        }

        if (twoDigitsAfterColon.test(time)) {
            const strippedTime = time.replace(/^:/, '');
            const num = Number(strippedTime);

            return num;
        }

        if (threeOrFourDigits.test(time)) {
            if (time.length === 3) {
                const hours = Number(time[0]);
                const minutes = Number(time[1] + time[2]);
                return Number(hours) * 60 + Number(minutes);
            }

            const hours = Number(time[0] + time[1]);
            const minutes = Number(time[2] + time[3]);
            return Number(hours) * 60 + Number(minutes);
        }

        return 0;
    }

    displayEmployee(employee: Employee | null): string {
        return employee?.fullName ?? '';
    }

    /**
     * updateTimesheetBasedOnHourType responds to an hour type being selected by updating
     * the required attributes on the timesheet entry. This is because "hour type" is not an actual
     * attribute of timesheet entries.
     */
    updateTimesheetBasedOnHourType(payrollHourType: PayrollHourType | null): void {
        this.timesheetEntry.salaryId = null;
        this.timesheetEntry.incomeType = null;
        this.timesheetEntry.incomeTypeId = null;

        if (!payrollHourType) {
            return;
        }

        if (payrollHourType.salaryId) {
            this.timesheetEntry.salaryId = payrollHourType.salaryId;
        } else {
            this.timesheetEntry.incomeType = payrollHourType.name;
            this.timesheetEntry.incomeTypeId = payrollHourType.id;
        }

        this.setSelectedHourType();
    }

    compareHourTypes(i1: PayrollHourType, i2: PayrollHourType | undefined | null): boolean {
        if (!i2) {
            return false;
        }

        return i1.name === i2.name && i1.id === i2.id && i1.salaryId === i2.salaryId;
    }

    onSelectStartTime(time: string): void {
        const date = this.timesheetEntry.workedOn || moment().format('YYYY-MM-DD');
        this.timesheetEntry.startTime = moment(`${date} ${time}`, 'YYYY-MM-DD h:mm A').format('YYYY-MM-DD HH:mm:ss');
        this.timesheetEntry.endTime = ''; // Clear the end time
        this.filterEndTimes(time);
        this.validateEndTime();
        this.updateMinutesBasedOnTimes();
    }

    onSelectEndTime(time: string): void {
        const date = this.timesheetEntry.workedOn || moment().format('YYYY-MM-DD');
        this.timesheetEntry.endTime = moment(`${date} ${time}`, 'YYYY-MM-DD h:mm A').format('YYYY-MM-DD HH:mm:ss');
        this.validateEndTime();
        this.updateMinutesBasedOnTimes();
    }

    filterStartTimes(value: string): void {
        this.filteredStartTimes = this.filterTimes(value);
    }

    /**
     * Filters the end times based on the start time.
     * This function is only applicable when the time tracking method is 'start_end_time'.
     */
    filterEndTimes(value: string): void {
        const timeFormatError = this.timeFormatValidator.validate({ value } as AbstractControl);

        if (timeFormatError) {
            this.filteredEndTimes = this.filterTimes(value);
            return;
        }

        this.filteredEndTimes = this.timeIntervals.filter(({ value: time }) => {
            const endTime = moment(time, 'h:mm A');
            const start = moment(value, 'h:mm A');
            return endTime.isAfter(start);
        });
    }

    /**
     * Updates the minutes worked based on the start and end times.
     * This function is only applicable when the time tracking method is 'start_end_time'.
     */
    updateMinutesBasedOnTimes(): void {
        if (
            this.settings.timeTrackingMethod !== 'start_end_time' ||
            !this.timesheetEntry.startTime ||
            !this.timesheetEntry.endTime
        ) {
            this.timesheetEntry.minutes = 0;
            this.updateFormattedHoursAndMinutes();
            this.onChangeMinutes.emit(this.timesheetEntry);
            return;
        }

        const start = moment(this.timesheetEntry.startTime, 'YYYY-MM-DD HH:mm:ss');
        let end = moment(this.timesheetEntry.endTime, 'YYYY-MM-DD HH:mm:ss');

        // If end time is 12 AM, treat it as end of day (23:59:59) plus one minute
        if (end.format('HH:mm') === '00:00') {
            end = end.startOf('day').add(1, 'day');
            this.timesheetEntry.endTime = end.format('YYYY-MM-DD HH:mm:ss');
        }

        // Calculate minutes and ensure we get a whole number
        const minutes = Math.floor(moment.duration(end.diff(start)).asMinutes());
        this.timesheetEntry.minutes = minutes;
        this.updateFormattedHoursAndMinutes();
        this.onChangeMinutes.emit(this.timesheetEntry);
    }

    updateFormattedHoursAndMinutes(): void {
        if (!this.timesheetEntry.minutes) {
            this.formattedHoursAndMinutes.set('0h 0m');
            return;
        }

        const hours = Math.floor(this.timesheetEntry.minutes / 60);
        const minutes = this.timesheetEntry.minutes % 60;
        this.formattedHoursAndMinutes.set(`${hours}h ${minutes}m`);
    }

    onEndTimeChange(time: string): void {
        if (!time) {
            this.timesheetEntry.endTime = '';
            return;
        }

        if (!this.isValidTimeFormat(time)) {
            return;
        }

        this.onSelectEndTime(time);
    }

    onStartTimeChange(time: string): void {
        if (!time) {
            this.timesheetEntry.startTime = '';
            return;
        }

        if (!this.isValidTimeFormat(time)) {
            return;
        }

        this.onSelectStartTime(time);
    }

    private async setHourTypes(): Promise<void> {
        if (!this.timesheetEntry.employee) {
            this.clearHourTypeData();
            return;
        }

        if (!this.companyIsOnPayrollIntegration) {
            return;
        }

        const freshHourTypes = await this.employeeHourTypesService.getHourTypes(
            this.timesheetEntry.employee,
            this.timesheetEntry.workedOn
        );

        // if the hour types have changed, we should clear all data
        const hourTypesHaveChanged =
            this.hourTypes.length !== freshHourTypes.length ||
            this.hourTypes.some((hourType, idx) => hourType.name !== freshHourTypes[idx].name);

        if (hourTypesHaveChanged && this.hourTypes.length) {
            // if the hour types have changed, and there were previously hour types
            // we need to clear hour type data
            // This handles the case where a date change could leave stale data.
            this.clearHourTypeData();
        }

        this.hourTypes = freshHourTypes;

        this.setSelectedHourType();
    }

    /**
     * calculateAllowNoProject checks if the no project option is valid.
     * It is not valid for some managers and project owners.
     */
    private calculateAllowNoProject(): void {
        const employee = this.timesheetEntry.employee;
        if (!employee) {
            this.allowNoProject = false;
            return;
        }

        if (this.authService.can(Platform.timeTracking.permission.view) || this.authService.employee?.is(employee)) {
            this.allowNoProject = true;
            return;
        }

        // not self, not admin, not manager? You are a project owner only. So no.
        if (employee.reportsTo !== this.authService.employee?.id) {
            this.allowNoProject = false;
            return;
        }

        this.allowNoProject = this.defaultApprovalFlow === 'manager';
    }

    /**
     * adjustProject checks if no project is allowed. If not, it sets a project.
     */
    private adjustProject(): void {
        // no employee yet, nothing to do
        if (!this.timesheetEntry.employee) {
            return;
        }

        // valid states
        if (this.allowNoProject || this.timesheetEntry.projectId) {
            return;
        }

        const assignments = this.timesheetEntry.employee.timeTrackingProjectAssignmentsUserCanEnterHoursFor;

        if (!assignments.length) {
            throw new Error('Must select a project, but none found for employee id: ' + this.timesheetEntry.employeeId);
        }

        this.timesheetEntry.projectId = assignments[0].projectId;
    }

    /**
     * handleFilteringEmployees sets up the observable that will monitor for changes to the name
     * field.
     */
    private handleFilteringEmployees(): void {
        this.filteredEmployees = this.employeeFormControl.valueChanges.pipe(
            tap(this.setEmployeeFromValue.bind(this)),
            map((value: string | Employee | null) => {
                const name = (typeof value === 'string' ? value : (value?.fullName ?? '')).toLowerCase();
                if (!name) {
                    return this.employees.slice();
                }

                return this.employees.filter((employee) => employee.fullName.toLowerCase().includes(name));
            })
        );
    }

    /**
     * setEmployeeFromValue inspect the current value in the observable
     * and sets the correct employee on the timesheet entry
     */
    private setEmployeeFromValue(value: string | Employee | null): void {
        const isEmployee = value && typeof value === 'object';
        if (isEmployee) {
            // the user has clicked an employee so we update the employee,
            // but only if the employee has changed
            if (this.timesheetEntry.employeeId !== value.id) {
                this.timesheetEntry.employeeId = value.id;
                this.onChangeEmployee(value.id);
            }
            return;
        }

        if (value && typeof value === 'string') {
            // the user has typed an employee's name
            // if it is exact, and the only match, select that employee
            const name = value.toLowerCase();
            const matches = this.employees.filter((e) => e.fullName.toLowerCase().includes(name));
            if (matches.length === 1 && matches[0].fullName.toLowerCase() === name) {
                this.timesheetEntry.employeeId = matches[0].id;
                this.onChangeEmployee(matches[0].id);
                return;
            }
        }

        this.onChangeEmployee(null);
    }

    /**
     * setSelectedHourType sets the hour type based on the attributes of the timesheet
     */
    private setSelectedHourType(): void {
        const entry = this.timesheetEntry;

        this.selectedHourType = this.hourTypes.find(
            (hourType: PayrollHourType) =>
                (entry.incomeType && entry.incomeType === hourType.name) ||
                (entry.incomeTypeId && entry.incomeTypeId === hourType.id) ||
                (entry.salaryId && entry.salaryId === hourType.salaryId)
        );

        if (!this.selectedHourType && this.hourTypes.length > 0) {
            const regularHourType = this.hourTypes.find(
                (hourType) => hourType.name.toLowerCase() === INCOME_TYPES.Regular
            );

            if (regularHourType) {
                this.selectedHourType = regularHourType;
                this.updateTimesheetBasedOnHourType(regularHourType);
            }
        }
    }

    /**
     * clearHourTypeData removes all hour type data from the timesheet entry
     * and from the drop down. This is useful for when an employee is changed.
     */
    private clearHourTypeData(): void {
        this.timesheetEntry.incomeType = null;
        this.timesheetEntry.incomeTypeId = null;
        this.timesheetEntry.salaryId = null;
        this.hourTypes = [];
        this.selectedHourType = undefined;
    }

    /**
     * setMaxDate determines the maximum date that can be workedOn, based on time tracking settings.
     */
    private async setMaxDate(): Promise<void> {
        if (this.settings.allowFutureEntries && this.settings.futureEntriesAllowedDays) {
            this.maxDate = addDays(new Date(), this.settings.futureEntriesAllowedDays);
        }
    }

    /**
     * Generates an array of time intervals for a 24-hour period in 15-minute increments.
     * This function populates the timeIntervals array with formatted time strings (e.g., "1:00 PM").
     * It uses lodash's range function to create an array of minute values from 0 to 1440 (24 hours * 60 minutes)
     * in steps of 15 minutes. Each value is then converted to a formatted time string using moment.js.
     */
    private generateTimeIntervals(): void {
        this.timeIntervals = generateTimeIntervals(this.currentLocale, this.settings, this.hasTimeClockTimer);
    }

    /**
     * Filters the time intervals based on the input value
     */
    private filterTimes(value: string): { value: string; label: string }[] {
        const filterValue = value.toLowerCase();
        return this.timeIntervals.filter(({ value: time }) => {
            const timeWithoutColon = time.replace(':', '');
            const timeWithoutColonAndPeriod = timeWithoutColon.replace(/\s[AP]M/, '');

            return (
                time.toLowerCase().includes(filterValue) ||
                timeWithoutColon.toLowerCase().includes(filterValue) ||
                timeWithoutColonAndPeriod.toLowerCase().includes(filterValue)
            );
        });
    }

    private isValidTimeFormat(time: string): boolean {
        return this.timeFormatValidator.validate({ value: time } as AbstractControl) === null;
    }

    private validateEndTime(): void {
        if (this.startTimeInput && this.endTimeInput) {
            // Set the start and end times on the validator instance
            this.endTimeValidator.startTime = this.timesheetEntry.startTime;
            this.endTimeValidator.endTime = this.timesheetEntry.endTime;

            // Call the validate method without arguments
            const errors = this.endTimeValidator.validate();

            // Set the errors on the end time input
            this.endTimeInput.control.setErrors(errors);
        }
    }
}
